import { scheduleIdle } from '../schedule-background.js';
export { scheduleAfterPaint as scheduleSearchWarmup } from '../schedule-background.js';

// The HTTP cache revalidates these stable URLs using the server's ETag. Keep
// only the parsed index shared within this document; a navigation checks again.
const indexes = new Map();

// An async module script keeps the background download at Low priority,
// including in browsers that ignore fetchpriority on modulepreload. It
// evaluates the engine's definitions after paint and fills the module map;
// the later import() reuses it before the separate idle index activation.
function loadEngine(url) {
  const script = document.createElement('script');
  script.type = 'module';
  script.async = true;
  script.setAttribute('fetchpriority', 'low');
  script.src = url;
  return new Promise((resolve, reject) => {
    script.onload = () => { script.remove(); resolve(); };
    script.onerror = () => { script.remove(); reject(new Error(`Failed to load search engine (${url})`)); };
    document.head.append(script);
  });
}

export function loadSearchIndex(url, priority = 'auto', engineUrl) {
  if (!engineUrl) throw new Error('Missing search engine URL');
  if (!indexes.has(url)) {
    let resolve, reject, cancelActivation;
    let started = false;
    const entry = {promise: new Promise((yes, no) => { resolve = yes; reject = no; })};
    indexes.set(url, entry);
    const fail = error => {
      cancelActivation?.();
      entry.start = null;
      if (indexes.get(url) === entry) indexes.delete(url);
      reject(error);
    };
    const body = (async () => {
      const response = await fetch(url, {cache: 'default', priority});
      if (!response.ok) throw new Error(`Failed to load search index (${response.status})`);
      return response.text();
    })();
    entry.start = () => {
      if (started) return;
      started = true;
      cancelActivation?.();
      Promise.all([import(/* @vite-ignore */ engineUrl), body])
        .then(([engine, text]) => engine.importIndex(JSON.parse(text)))
        .then(index => { entry.start = null; resolve(index); }, fail);
    };
    if (priority === 'low') {
      // Download the independent engine and index together at Low priority.
      // Wait for both before import() so it consumes the prepared module
      // without starting a new High-priority request. Intent bypasses the
      // idle wait and uses that same module map and index promise.
      Promise.all([loadEngine(engineUrl), body]).then(() => {
        if (!started) cancelActivation = scheduleIdle(entry.start);
      }, error => { if (!started) fail(error); });
    }
  }
  const entry = indexes.get(url);
  if (priority !== 'low') entry.start?.();
  return entry.promise;
}
